//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion

import { EventsNames, LocalizationHelper, TypeHelper } from '../../../../helpers/'
import { PopupHelper } from '../../../../helpers/popup-helper'
import { Enums } from '../../../../models/common/enums'
import { ConfirmationOptions } from '../../confirmation-popup/confirmation-popup.directive'
import { IItemSwapper, ItemSwapperOptions, ItemSwapperResult } from '../../item-swapper'
import { UserSessionService } from '../../../../services/user-session'
import { IMRConfirmationPopupOptions, IMRConfirmationPopupInitializedArgs, IIMRConfirmationPopup } from '../../confirmation-popup2';
import { IMRBaseControlDirectiveController } from '../../base';

// #region Options

// #region IItemSwapperPopup<T>

export interface IItemSwapperPopup<T> {
    show(): void
    hide(): void

    isVisible(): boolean

    itemSwapper: IItemSwapper<T>
}

// #endregion

// #region ItemSwapperPopupConfirmationOptions

type ItemSwapperPopupConfirmationOptions = {
    title?: string
    question?: string
}

// #endregion

// #region ItemSwapperPopupTextButtonOptions

type ItemSwapperPopupTextButtonOptions = {
    hint?: string
    text?: string
}

// #endregion

// #region ItemSwapperPopupOptions<TSourceObject>

export type ItemSwapperPopupOptions<TSourceObject> = {
    referenceType?: Enums.ReferenceType

    itemSwapperPopupTitle: string | (() => string)

    buttonOptions?: {
        save?: ItemSwapperPopupTextButtonOptions
    }

    confirmationOptions?: {
        change?: ItemSwapperPopupConfirmationOptions
        save?: ItemSwapperPopupConfirmationOptions
    }

    itemSwapperOptions: ItemSwapperOptions<TSourceObject>

    /**
     * Called when pressed the save button and there are changes to save.
     */
    onSave?: (result: ItemSwapperResult<TSourceObject>) => JQueryPromise<any>

    /**
     * Called when a change of content happens.
     */
    onUpdate?: (result: ItemSwapperResult<TSourceObject>) => void

    /**
     * Called when pressed the save button and there are no changes to save.
     */
    onCancel?: () => void

    onInitialized?: (result: IItemSwapperPopup<any>) => void
}

// #endregion

// #region ItemSwapperPopupScope<TSourceObject>

export interface ItemSwapperPopupScope<TSourceObject> extends ng.IScope {
    id: number

    options: ItemSwapperPopupOptions<TSourceObject>
    isLoading: boolean
    isInitializing: boolean
    isPopupReady: boolean

    confirmationOptions: IMRConfirmationPopupOptions
    closeConfirmationOptions: IMRConfirmationPopupOptions

    saveButtonOptions: DevExpress.ui.dxButtonOptions

    itemSwapperOptions: ItemSwapperOptions<TSourceObject>

    itemSwapperPopupOptions: DevExpress.ui.dxPopupOptions
    itemSwapperPopup: DevExpress.ui.dxPopup
}

// #endregion

export class ItemSwapperPopupController<TSourceObject>
    extends IMRBaseControlDirectiveController
    implements IItemSwapperPopup<any>
{

    // #region Consts

    private get LOCALIZATION_COLLECTION_NAME(): string { return 'ItemSwapper' }

    // #endregion

    // #region Localization Consts

    private localizationText = class {
        public static get AreYouSure(): string { return 'AREYOUSURE' }
        public static get AreYouSureYouWantToSelectDifferentElement(): string { return 'AreYouSureYouWantToSelectDifferentElement' }
        public static get Save(): string { return 'APPLY' }
        public static get SavingChanges(): string { return 'SAVINGCHANGES' }
        public static get UnsavedChanges(): string { return 'UNSAVEDCHANGES' }
        public static get AreYouSureToClose(): string { return 'AreYouSureToClose' }
    }

    // #endregion

    // #region Members

    private isSubmitting: boolean = false

    private applyButton: DevExpress.ui.dxButton
    private saveButton: DevExpress.ui.dxButton

    private isHidingCanceled: boolean

    private itemSwapperInternal: IItemSwapper<TSourceObject>
    private confirmationPopup: IIMRConfirmationPopup
    private closeConfirmationPopup: IIMRConfirmationPopup

    // #endregion

    // #region Properties

    private get hasChanges(): boolean {
        return this.itemSwapperInternal != null && this.itemSwapperInternal.hasChanges()
    }

    private get isReady(): boolean {
        return this.itemSwapperInternal != null && this.itemSwapperInternal.isReady()
    }

    // #endregion

    // #region Options Properties

    private get saveConfirmationTitleOption(): string {
        return this.$scope.options.confirmationOptions && this.$scope.options.confirmationOptions.save && this.$scope.options.confirmationOptions.save.title
            || this.getLocalizedText(this.localizationText.SavingChanges)
    }

    private get saveConfirmationQuestionOption(): string {
        return this.$scope.options.confirmationOptions && this.$scope.options.confirmationOptions.save && this.$scope.options.confirmationOptions.save.question
            || this.getLocalizedText(this.localizationText.AreYouSure)
    }

    private get changeConfirmationTitleOption(): string {
        return this.$scope.options.confirmationOptions && this.$scope.options.confirmationOptions.change && this.$scope.options.confirmationOptions.change.title
            || this.getLocalizedText(this.localizationText.UnsavedChanges)
    }

    // #endregion

    // #region Ctor

    constructor(private $scope: ItemSwapperPopupScope<TSourceObject>, private userSession: UserSessionService) {
        super()

        this.$scope.isLoading = true
        this.$scope.isInitializing = true

        this.initialize()

    }

    // #endregion

    // #region IItemSwapperPopup Implementation

    public get itemSwapper(): IItemSwapper<TSourceObject> { return this.itemSwapperInternal }

    public isVisible(): boolean {
        return this.$scope != null
            && this.$scope.itemSwapperPopup != null
            && this.$scope.itemSwapperPopup.option("visible")
    }

    public show(): void {
        this.$scope.itemSwapperPopup.show()

        if (this.itemSwapper != null) {
            this.itemSwapper.show()
        }
    }

    public hide(): void {
        this.reset()

        if (this.itemSwapper != null) {
            this.itemSwapper.hide()
        }

        this.$scope.itemSwapperPopup.hide()
    }

    private initialize(): void {
        this.validateOptions(this.$scope.options)

        this.$scope.isInitializing = false

        this.setButtonsOptions()
        this.initializeConfirmationPopup()
        this.initializeCloseConfirmationPopup()

        this.setPopupOptions()

        this.setItemSwapperOptions().then(() => {
            this.onInitialized()
        })
    }

    private onInitialized(): void {
        if (this.$scope.options != null && _.isFunction(this.$scope.options.onInitialized)) {
            this.$scope.options.onInitialized(this)
        }
    }

    private setItemSwapperOptions(): JQueryPromise<void> {
        const deferred: JQueryDeferred<void> = $.Deferred<void>()

        if (this.$scope.options.itemSwapperOptions != null) {
            if (this.$scope.options.itemSwapperOptions.onInitialized != null) {
                const existingFunc: (result: IItemSwapper<any>) => void = this.$scope.options.itemSwapperOptions.onInitialized

                this.$scope.options.itemSwapperOptions.onInitialized = (component: IItemSwapper<TSourceObject>) => {
                    this.itemSwapperInternal = component

                    this.$scope.isLoading = false

                    this.itemSwapper.toggleButtons()

                    existingFunc(component)
                    deferred.resolve()
                }
            } else {
                this.$scope.options.itemSwapperOptions.onInitialized = (component: IItemSwapper<TSourceObject>) => {
                    this.itemSwapperInternal = component

                    this.$scope.isLoading = false

                    this.itemSwapper.toggleButtons()

                    deferred.resolve()
                }
            }

            if (this.$scope.options.itemSwapperOptions.onUpdate != null) {
                const existingFunc: (result: ItemSwapperResult<TSourceObject>) => void = this.$scope.options.itemSwapperOptions.onUpdate

                this.$scope.options.itemSwapperOptions.onUpdate = (result: ItemSwapperResult<TSourceObject>) => {
                    this.updateTitle()
                    this.toggleButtons()
                    existingFunc(result)
                }
            } else {
                this.$scope.options.itemSwapperOptions.onUpdate = (result: ItemSwapperResult<TSourceObject>) => {
                    this.updateTitle()
                    this.toggleButtons()
                }
            }

            this.$scope.itemSwapperOptions = this.$scope.options.itemSwapperOptions
        }

        return deferred.promise()
    }

    // #endregion

    // #region Lifecycle

    private setPopupOptions(): void {
        const title: string = TypeHelper.isFunction(this.$scope.options.itemSwapperPopupTitle)
            ? (<any>this.$scope.options.itemSwapperPopupTitle)()
            : this.$scope.options.itemSwapperPopupTitle

        this.$scope.itemSwapperPopupOptions = PopupHelper.mergeWithDefaultOptions({
            title: title,
            onInitialized: (e: any) => {
                this.$scope.itemSwapperPopup = e.component
            },
            onHiding: (e: any) => { this.hiding(e) },
            onHidden: () => {
                this.isHidingCanceled = false
                this.$scope.isPopupReady = false
            },
            onShown: () => {
                this.itemSwapper.clearSelectGridValue()
                this.itemSwapper.repaint()

                this.$scope.isPopupReady = true
            }
        })
    }

    private hiding(e: any): void {
        if (this.hasChanges && !this.isHidingCanceled) {
            e.cancel = true

            if (this.closeConfirmationPopup != null) {
                this.closeConfirmationPopup.show()
            }
        } else {
            this.hide()
        }
    }

    // #endregion

    // #region ItemSwapperController

    private reset(): void {
        this.itemSwapper.reset()
        this.toggleButtons()
    }

    private submit(): void {
        if (this.$scope.options.onSave != null && !this.isSubmitting) {
            this.$scope.isLoading = true
            this.isSubmitting = true

            this.toggleButtons()

            this.$scope.options
                .onSave(this.itemSwapper.getSwapResult())
                .fail((error: any) => {
                    return $.Deferred().reject(error)
                })
                .always(() => {
                    this.reset()

                    this.$scope.isLoading = false
                    this.isSubmitting = false
                })
        }
    }

    private updateTitle(): void {
        if (this.$scope.options != null
            && TypeHelper.isFunction(this.$scope.options.itemSwapperPopupTitle)
            && this.$scope.itemSwapperPopup != null
        ) {
            this.$scope.itemSwapperPopup.option('title', (<any>this.$scope.options.itemSwapperPopupTitle)())
        }
    }

    // #endregion

    // #region Events

    // #endregion

    // #region Button Methods

    private toggleApplyButton(): void {
        if (this.applyButton) {
            this.applyButton.option('disabled', !this.isReady || !this.hasChanges || this.isSubmitting)
        }
    }

    private toggleSaveButton(): void {
        if (this.saveButton) {
            this.saveButton.option('disabled', !this.isReady || !this.hasChanges || this.isSubmitting)
        }
    }

    private toggleButtons(): void {
        this.toggleApplyButton()
        this.toggleSaveButton()
    }

    // #endregion

    // #region Button Events

    private onApply(): void {
        if (this.hasChanges) {
            this.confirmationPopup.show()
        }
        else {
            this.onCancel()
        }
    }

    private onCancel(): void {
        if (this.$scope.options.onCancel != null) {
            this.$scope.options.onCancel()
        }
    }

    private onSave(): void {
        this.onApply()
    }

    // #endregion

    //#region Confirmation Popup

    private initializeConfirmationPopup() {
        this.$scope.confirmationOptions = {
            title: this.changeConfirmationTitleOption,
            content: this.saveConfirmationQuestionOption,
            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            },
            onConfirmed: () => {
                this.submit()
            }
        }
    }

    private initializeCloseConfirmationPopup() {
        this.$scope.closeConfirmationOptions = {
            title: this.changeConfirmationTitleOption,
            content: this.getLocalizedText(this.localizationText.AreYouSureToClose),
            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.closeConfirmationPopup = e.component
            },
            onConfirmed: () => {
                this.submit()
            },
            onDeclined: () => {
                this.isHidingCanceled = true
                this.$scope.itemSwapperPopup.hide()
            }
        }
    }

    //#endregion

    // #region Setup

    private getLocalizedText(text: string): string {
        return LocalizationHelper.getLocalizedText(text, { collection: this.LOCALIZATION_COLLECTION_NAME })
    }

    private setButtonsOptions(): void {
        this.$scope.saveButtonOptions = {
            hint: this.$scope.options.buttonOptions && this.$scope.options.buttonOptions.save && this.$scope.options.buttonOptions.save.hint,
            text: this.$scope.options.buttonOptions && this.$scope.options.buttonOptions.save && this.$scope.options.buttonOptions.save.text
                || this.getLocalizedText(this.localizationText.Save),

            onInitialized: (e: any) => {
                this.saveButton = e.component
                this.toggleButtons()
            },

            onClick: () => this.onSave()
        }
    }

    private validateOptions(options: ItemSwapperPopupOptions<TSourceObject>): void {
        if (options == null) {
            throw new Error('The options object cannot be undefined.')
        }

        if (options.itemSwapperOptions == null) {
            throw new Error('ItemSwapper options object cannot be undefined.')
        }
    }

    // #endregion
}